import { useState, useEffect, useRef, useCallback } from "react";
import { useLocation } from "react-router-dom";
import { Search, Tv, Volume2, VolumeX, Maximize, Minimize, Radio, Clock, RotateCcw, Play, Pause, Monitor } from "lucide-react";
import Hls from "hls.js";
import api from "@/services/api";
import { proxyImageUrl } from "@/utils/imageProxy";
import { addToHistory } from "@/utils/watchData";

const LAST_CHANNEL_KEY = "fibernet_last_channel";

const resolveStreamUrl = (url) => {
  if (!url) return null;
  return url.replace("http://", "https://");
};

const extractStreamToken = (m3u8Url) => {
  if (!m3u8Url) return null;
  const parts = m3u8Url.split('/');
  const lastPart = parts[parts.length - 1];
  return lastPart.replace(/\.(m3u8|ts)$/, '');
};

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const buildArchiveUrl = (channel, offsetSeconds) => {
  const archiveId = channel?.tv_archive_id;
  if (!archiveId) return null;
  const m3u8Url = resolveStreamUrl(channel.links?.m3u8);
  const token = extractStreamToken(m3u8Url);
  if (!token) return null;
  return `${BACKEND_URL}/api/archive-playlist?archive_id=${archiveId}&token=${token}&offset=${Math.floor(offsetSeconds)}`;
};

const formatClockTime = (timestamp) => {
  if (!timestamp) return '';
  const d = new Date(timestamp * 1000);
  return d.toLocaleTimeString('az-AZ', { hour: '2-digit', minute: '2-digit', second: '2-digit' });
};

const formatClockTimeShort = (timestamp) => {
  if (!timestamp) return '';
  const d = new Date(timestamp * 1000);
  return d.toLocaleTimeString('az-AZ', { hour: '2-digit', minute: '2-digit' });
};

export default function LiveTVPage() {
  const location = useLocation();
  const [channels, setChannels] = useState([]);
  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [activeChannel, setActiveChannel] = useState(null);
  const [epg, setEpg] = useState([]);
  const [mobileChannelsOpen, setMobileChannelsOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [streamLoading, setStreamLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [muted, setMuted] = useState(false);
  const [volume, setVolume] = useState(0.2);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [showControls, setShowControls] = useState(true);
  const [isCatchup, setIsCatchup] = useState(false);
  const [timeshiftStartTs, setTimeshiftStartTs] = useState(null);
  const [seekPosition, setSeekPosition] = useState(1);
  const [isDragging, setIsDragging] = useState(false);
  const [seekHoverPos, setSeekHoverPos] = useState(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [showPlayPauseIcon, setShowPlayPauseIcon] = useState(null);
  const [catchupProgram, setCatchupProgram] = useState(null);
  const [thumbnailData, setThumbnailData] = useState(null);
  const [thumbnailCache, setThumbnailCache] = useState([]);
  const [currentDisplayTime, setCurrentDisplayTime] = useState('');

  const videoRef = useRef(null);
  const hlsRef = useRef(null);
  const freezeCanvasRef = useRef(null);
  const containerRef = useRef(null);
  const controlTimerRef = useRef(null);
  const channelListRef = useRef(null);
  const seekBarRef = useRef(null);
  const playPauseTimerRef = useRef(null);
  const mouseInsideRef = useRef(false);

  // Fetch channels and EPG
  useEffect(() => {
    const fetchData = async () => {
      try {
        const [channelsData, categoriesData] = await Promise.all([
          api.getLiveContent({ per_page: 500 }),
          api.getLiveCategories(),
        ]);
        const sorted = (channelsData.content || []).sort((a, b) => (a.num || 0) - (b.num || 0));
        setChannels(sorted);
        setCategories(categoriesData.content || []);
        // Priority: navigation state (e.g. from HomePage) > last watched > first
        const requestedId = location.state?.channelId || new URLSearchParams(location.search).get("channel");
        const requestedChannel = requestedId && sorted.find((c) => c.stream_id == requestedId);
        if (requestedChannel) {
          playChannel(requestedChannel);
        } else {
          const lastId = localStorage.getItem(LAST_CHANNEL_KEY);
          const lastChannel = lastId && sorted.find((c) => c.stream_id == lastId);
          if (lastChannel) playChannel(lastChannel, false);
          else if (sorted.length > 0) playChannel(sorted[0], false);
        }
      } catch (error) {
        console.error("Failed to fetch channels:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
    return () => {
      destroyHls();
      const video = videoRef.current;
      if (video) { video.removeAttribute('src'); video.load(); }
    };
  }, []);

  // Fullscreen change
  useEffect(() => {
    const handleFSChange = () => setIsFullscreen(!!document.fullscreenElement);
    document.addEventListener("fullscreenchange", handleFSChange);
    return () => document.removeEventListener("fullscreenchange", handleFSChange);
  }, []);

  // Auto-hide controls
  const handlePlayerMouseMove = useCallback(() => {
    mouseInsideRef.current = true;
    setShowControls(true);
    clearTimeout(controlTimerRef.current);
    controlTimerRef.current = setTimeout(() => {
      if (mouseInsideRef.current) setShowControls(false);
    }, 3000);
  }, []);

  const handlePlayerMouseLeave = useCallback(() => {
    mouseInsideRef.current = false;
    clearTimeout(controlTimerRef.current);
    setShowControls(false);
  }, []);

  const handlePlayerMouseEnter = useCallback(() => {
    mouseInsideRef.current = true;
    setShowControls(true);
    clearTimeout(controlTimerRef.current);
    controlTimerRef.current = setTimeout(() => setShowControls(false), 3000);
  }, []);

  // Track play/pause state
  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;
    const onPlay = () => setIsPlaying(true);
    const onPause = () => setIsPlaying(false);
    video.addEventListener('play', onPlay);
    video.addEventListener('pause', onPause);
    return () => { video.removeEventListener('play', onPlay); video.removeEventListener('pause', onPause); };
  }, []);

  const destroyHls = () => {
    if (hlsRef.current) {
      hlsRef.current.off(Hls.Events.ERROR);
      hlsRef.current.destroy();
      hlsRef.current = null;
    }
    const video = videoRef.current;
    if (video) { video.pause(); }
  };

  // Capture last frame to canvas before switching channels (avoid black flash)
  const captureFreezeFrame = useCallback(() => {
    const video = videoRef.current;
    const canvas = freezeCanvasRef.current;
    if (!video || !canvas || video.readyState < 2 || !video.videoWidth) return;
    try {
      canvas.width = video.videoWidth;
      canvas.height = video.videoHeight;
      const ctx = canvas.getContext('2d');
      ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
      // Show instantly (no fade-in) — otherwise black flash bleeds through
      canvas.style.transition = 'none';
      canvas.style.opacity = '1';
    } catch { /* CORS or other error — ignore */ }
  }, []);

  const hideFreezeFrame = useCallback(() => {
    const canvas = freezeCanvasRef.current;
    if (!canvas) return;
    canvas.style.transition = 'none';
    canvas.style.opacity = '0';
  }, []);

  const startPlayback = useCallback((streamUrl) => {
    if (!streamUrl) return;
    setStreamLoading(true);
    const video = videoRef.current;
    if (!video) return;
    destroyHls();
    const isArchive = streamUrl.includes('archive-playlist') || streamUrl.includes('arc1.fiber');
    const isHls = streamUrl.includes(".m3u8") || streamUrl.includes("archive-playlist");
    if (isHls && Hls.isSupported()) {
      const hls = new Hls({
        enableWorker: true,
        lowLatencyMode: !isArchive,
        liveDurationInfinity: !isArchive,
        liveBackBufferLength: isArchive ? 0 : 30,
        maxBufferLength: 30,
        maxMaxBufferLength: 60,
        stretchShortVideoTrack: true,
        maxAudioFramesDrift: 1,
      });
      hls.loadSource(streamUrl);
      hls.attachMedia(video);
      hls.on(Hls.Events.MANIFEST_PARSED, () => {
        setStreamLoading(false);
        video.volume = volume;
        video.muted = true;
        video.play().then(() => {
          setIsPlaying(true);
          video.muted = false;
          setMuted(false);
          hideFreezeFrame();
        }).catch(() => {
          setMuted(true);
          setIsPlaying(false);
          hideFreezeFrame();
        });
      });
      hls.on(Hls.Events.ERROR, (_, data) => {
        if (data.fatal) {
          if (data.type === Hls.ErrorTypes.MEDIA_ERROR) hls.recoverMediaError();
          else if (data.type === Hls.ErrorTypes.NETWORK_ERROR) hls.startLoad();
          else destroyHls();
        }
      });
      hlsRef.current = hls;
    } else if (video.canPlayType("application/vnd.apple.mpegurl")) {
      video.src = streamUrl;
      video.addEventListener("loadedmetadata", () => {
        setStreamLoading(false);
        video.play().then(() => hideFreezeFrame()).catch(() => hideFreezeFrame());
      }, { once: true });
    }
  }, [volume, hideFreezeFrame]);

  const playChannel = useCallback((channel, trackHistory = true) => {
    if (!channel) return;
    captureFreezeFrame();
    setActiveChannel(channel);
    setIsCatchup(false);
    setTimeshiftStartTs(null);
    setCatchupProgram(null);
    setSeekPosition(1);
    setThumbnailData(null);
    localStorage.setItem(LAST_CHANNEL_KEY, channel.stream_id);
    if (trackHistory) {
      addToHistory({
        id: channel.stream_id,
        name: channel.name,
        poster: channel.stream_icon,
        type: 'live',
      });
    }
    fetchEpg(channel.stream_id);
    const streamUrl = resolveStreamUrl(channel.links?.m3u8);
    startPlayback(streamUrl);
  }, [startPlayback, captureFreezeFrame]);

  const playCatchup = useCallback((program) => {
    if (!activeChannel || !program.has_archive) return;
    const startTs = parseInt(program.start_timestamp) || Math.floor(Date.now() / 1000);
    const stopTs = parseInt(program.stop_timestamp) || startTs + 3600;
    const now = Math.floor(Date.now() / 1000);
    const offsetSeconds = now - startTs;
    const archiveUrl = buildArchiveUrl(activeChannel, offsetSeconds);
    if (!archiveUrl) return;
    setIsCatchup(true);
    setTimeshiftStartTs(startTs);
    setCatchupProgram({
      start_timestamp: startTs,
      stop_timestamp: Math.min(stopTs, now),
      title: decodeBase64(program.title),
    });
    setSeekPosition(0);
    setThumbnailData(null);
    startPlayback(archiveUrl);
  }, [activeChannel, startPlayback]);

  const backToLive = useCallback(() => {
    if (!activeChannel) return;
    setIsCatchup(false);
    setTimeshiftStartTs(null);
    setCatchupProgram(null);
    setSeekPosition(1);
    setThumbnailData(null);
    const streamUrl = resolveStreamUrl(activeChannel.links?.m3u8);
    startPlayback(streamUrl);
  }, [activeChannel, startPlayback]);

  const handleSeek = useCallback((posRatio) => {
    if (!activeChannel) return;
    const pos = Math.max(0, Math.min(1, posRatio));

    if (catchupProgram) {
      const programDuration = catchupProgram.stop_timestamp - catchupProgram.start_timestamp;
      if (programDuration <= 0) return;
      const targetTs = catchupProgram.start_timestamp + pos * programDuration;
      const now = Math.floor(Date.now() / 1000);
      const offsetFromNow = now - Math.floor(targetTs);
      if (offsetFromNow <= 5) { backToLive(); return; }
      const archiveUrl = buildArchiveUrl(activeChannel, offsetFromNow);
      if (!archiveUrl) return;
      setTimeshiftStartTs(Math.floor(targetTs));
      setSeekPosition(pos);
      startPlayback(archiveUrl);
    } else {
      const archiveSec = (activeChannel.tv_archive_duration || 0) * 3600;
      if (archiveSec <= 0) return;
      if (pos >= 0.998) { backToLive(); return; }
      const offsetSeconds = Math.floor((1 - pos) * archiveSec);
      const archiveUrl = buildArchiveUrl(activeChannel, offsetSeconds);
      if (!archiveUrl) return;
      setTimeshiftStartTs(Math.floor(Date.now() / 1000) - offsetSeconds);
      setSeekPosition(pos);
      setIsCatchup(true);
      startPlayback(archiveUrl);
    }
  }, [activeChannel, catchupProgram, startPlayback, backToLive]);

  const handleSeekBarMouseDown = useCallback((e) => {
    e.preventDefault();
    setIsDragging(true);
    const getPos = (clientX) => {
      if (!seekBarRef.current) return 0;
      const rect = seekBarRef.current.getBoundingClientRect();
      return Math.max(0, Math.min(1, (clientX - rect.left) / rect.width));
    };
    setSeekPosition(getPos(e.clientX));
    const onMove = (ev) => setSeekPosition(getPos(ev.clientX));
    const onUp = (ev) => {
      setIsDragging(false);
      handleSeek(getPos(ev.clientX));
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
    };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
  }, [handleSeek]);

  const handleSeekBarHover = useCallback((e) => {
    if (!seekBarRef.current || isDragging) return;
    const rect = seekBarRef.current.getBoundingClientRect();
    const pos = Math.max(0, Math.min(1, (e.clientX - rect.left) / rect.width));
    setSeekHoverPos(pos);
  }, [isDragging]);

  const handleSeekBarLeave = useCallback(() => {
    setSeekHoverPos(null);
  }, []);

  // Find closest cached thumbnail for hover position
  const getClosestThumbnail = useCallback((hoverPos) => {
    if (thumbnailCache.length === 0) return null;
    let closest = thumbnailCache[0];
    let minDist = Math.abs(closest.pos - hoverPos);
    for (const thumb of thumbnailCache) {
      const dist = Math.abs(thumb.pos - hoverPos);
      if (dist < minDist) { closest = thumb; minDist = dist; }
    }
    return minDist < 0.08 ? closest : null;
  }, [thumbnailCache]);

  // Preload thumbnails when catch-up starts
  useEffect(() => {
    if (!catchupProgram || !activeChannel) {
      setThumbnailCache([]);
      return;
    }
    let cancelled = false;
    const cache = [];
    const positions = [0.05, 0.15, 0.25, 0.35, 0.45, 0.55, 0.65, 0.75, 0.85, 0.95];

    const loadThumbnail = (pos) => {
      if (cancelled) return Promise.resolve();
      const programDuration = catchupProgram.stop_timestamp - catchupProgram.start_timestamp;
      const targetTs = catchupProgram.start_timestamp + pos * programDuration;
      const now = Math.floor(Date.now() / 1000);
      const offsetFromNow = now - Math.floor(targetTs);
      if (offsetFromNow <= 0) return Promise.resolve();
      const archiveUrl = buildArchiveUrl(activeChannel, offsetFromNow);
      if (!archiveUrl) return Promise.resolve();

      return new Promise((resolve) => {
        const video = document.createElement('video');
        video.muted = true;
        video.playsInline = true;
        video.style.cssText = 'position:fixed;width:1px;height:1px;opacity:0;pointer-events:none;';
        document.body.appendChild(video);

        const hls = new Hls({ enableWorker: false, maxBufferLength: 3, maxMaxBufferLength: 5 });
        const cleanup = () => {
          hls.destroy();
          if (video.parentNode) video.parentNode.removeChild(video);
        };
        const timeout = setTimeout(() => { cleanup(); resolve(); }, 6000);

        hls.loadSource(archiveUrl);
        hls.attachMedia(video);
        hls.on(Hls.Events.MANIFEST_PARSED, () => {
          video.addEventListener('loadeddata', () => {
            try {
              const canvas = document.createElement('canvas');
              canvas.width = 192;
              canvas.height = 108;
              canvas.getContext('2d').drawImage(video, 0, 0, 192, 108);
              cache.push({ pos, url: canvas.toDataURL('image/jpeg', 0.5) });
              if (!cancelled) setThumbnailCache([...cache]);
            } catch (e) { /* canvas capture failed */ }
            clearTimeout(timeout);
            cleanup();
            resolve();
          }, { once: true });
        });
        hls.on(Hls.Events.ERROR, (_, data) => {
          if (data.fatal) { clearTimeout(timeout); cleanup(); resolve(); }
        });
      });
    };

    const loadAll = async () => {
      // Load 2 thumbnails at a time for faster completion
      for (let i = 0; i < positions.length; i += 2) {
        if (cancelled) break;
        const batch = positions.slice(i, i + 2).map(pos => loadThumbnail(pos));
        await Promise.all(batch);
        if (!cancelled && i + 2 < positions.length) await new Promise(r => setTimeout(r, 100));
      }
    };
    // Start preloading after a short delay to let the main video load first
    const startTimer = setTimeout(() => loadAll(), 1500);

    return () => {
      cancelled = true;
      clearTimeout(startTimer);
      setThumbnailCache([]);
    };
  }, [catchupProgram, activeChannel]);

  const getHoverTime = useCallback((hoverPos) => {
    if (catchupProgram) {
      const programDuration = catchupProgram.stop_timestamp - catchupProgram.start_timestamp;
      const targetTs = catchupProgram.start_timestamp + hoverPos * programDuration;
      return formatClockTimeShort(targetTs);
    }
    const archiveSec = (activeChannel?.tv_archive_duration || 0) * 3600;
    const offsetSec = Math.round((1 - hoverPos) * archiveSec);
    const target = new Date(Date.now() - offsetSec * 1000);
    return target.toLocaleTimeString('az-AZ', { hour: '2-digit', minute: '2-digit' });
  }, [catchupProgram, activeChannel]);

  const fetchEpg = async (streamId) => {
    try {
      const data = await api.getChannelFullEpg(streamId);
      setEpg(data.epg_listings || []);
    } catch (err) {
      setEpg([]);
    }
  };

  const toggleMute = useCallback(() => {
    const video = videoRef.current;
    if (!video) return;
    video.muted = !video.muted;
    setMuted(video.muted);
  }, []);

  const changeVolume = useCallback((val) => {
    const video = videoRef.current;
    if (!video) return;
    const v = Math.max(0, Math.min(1, val));
    video.volume = v;
    setVolume(v);
    if (v === 0) { video.muted = true; setMuted(true); }
    else if (video.muted) { video.muted = false; setMuted(false); }
  }, []);

  const toggleFullscreen = async () => {
    const el = containerRef.current;
    if (!el) return;
    if (document.fullscreenElement) {
      try { if (screen.orientation?.unlock) screen.orientation.unlock(); } catch { /* ignore */ }
      await document.exitFullscreen();
    } else {
      await el.requestFullscreen();
      // Force landscape on mobile devices that support orientation lock
      try { if (screen.orientation?.lock) await screen.orientation.lock('landscape'); } catch { /* ignore — desktop/iOS */ }
    }
  };

  const togglePiP = async () => {
    try {
      if (document.pictureInPictureElement) await document.exitPictureInPicture();
      else if (videoRef.current) await videoRef.current.requestPictureInPicture();
    } catch (err) { /* PiP not supported */ }
  };

  const togglePlayPause = useCallback(() => {
    const video = videoRef.current;
    if (!video) return;
    if (video.paused) {
      video.play().then(() => { setIsPlaying(true); }).catch(() => {});
      setShowPlayPauseIcon('play');
    } else {
      video.pause();
      setIsPlaying(false);
      setShowPlayPauseIcon('pause');
    }
    clearTimeout(playPauseTimerRef.current);
    playPauseTimerRef.current = setTimeout(() => setShowPlayPauseIcon(null), 800);
  }, []);

  const handleVideoClick = useCallback((e) => {
    if (e.target.closest('button') || e.target.closest('input') || e.target.closest('[data-controls]')) return;
    togglePlayPause();
  }, [togglePlayPause]);

  // Keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (!channels.length || !activeChannel) return;
      const idx = channels.findIndex((c) => c.stream_id === activeChannel.stream_id);
      if (e.key === "ArrowUp" && idx > 0) { e.preventDefault(); playChannel(channels[idx - 1]); }
      else if (e.key === "ArrowDown" && idx < channels.length - 1) { e.preventDefault(); playChannel(channels[idx + 1]); }
      else if (e.key === "ArrowRight") { e.preventDefault(); changeVolume(volume + 0.1); }
      else if (e.key === "ArrowLeft") { e.preventDefault(); changeVolume(volume - 0.1); }
      else if (e.key === "m" || e.key === "M") toggleMute();
      else if (e.key === "f" || e.key === "F") toggleFullscreen();
      else if (e.key === " ") { e.preventDefault(); togglePlayPause(); }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [channels, activeChannel, playChannel, toggleMute, changeVolume, volume, togglePlayPause]);

  // Scroll active channel into view (only inside channel list, not the whole page)
  useEffect(() => {
    if (activeChannel && channelListRef.current) {
      const el = channelListRef.current.querySelector(`[data-testid="channel-item-${activeChannel.stream_id}"]`);
      if (!el) return;
      const container = channelListRef.current;
      const containerRect = container.getBoundingClientRect();
      const elRect = el.getBoundingClientRect();
      // Offset of element relative to container scroll
      const relativeTop = elRect.top - containerRect.top + container.scrollTop;
      container.scrollTo({
        top: relativeTop - container.clientHeight / 2 + el.offsetHeight / 2,
        behavior: 'smooth',
      });
    }
  }, [activeChannel]);

  // Determine which EPG program is currently being watched
  const watchingProgramIndex = catchupProgram
    ? epg.findIndex((p) => {
        const pStart = parseInt(p.start_timestamp) || 0;
        const pStop = parseInt(p.stop_timestamp) || 0;
        return pStart === catchupProgram.start_timestamp || (pStart <= catchupProgram.start_timestamp && pStop > catchupProgram.start_timestamp);
      })
    : epg.findIndex((p) => p.now_playing === 1);

  // Scroll EPG to the currently watched program, centered in the visible area
  useEffect(() => {
    if (epg.length > 0) {
      setTimeout(() => {
        const epgEl = document.getElementById("epg-scroll");
        const target = epgEl?.querySelector("[data-watching='true']") || epgEl?.querySelector("[data-now='true']");
        if (!epgEl || !target) return;
        const headerH = epgEl.querySelector("[data-epg-header]")?.offsetHeight || 0;
        const containerRect = epgEl.getBoundingClientRect();
        const elRect = target.getBoundingClientRect();
        const relativeTop = elRect.top - containerRect.top + epgEl.scrollTop;
        const visibleCenter = headerH + (epgEl.clientHeight - headerH) / 2;
        epgEl.scrollTo({
          top: relativeTop - visibleCenter + target.offsetHeight / 2,
          behavior: 'smooth',
        });
      }, 100);
    }
  }, [epg, watchingProgramIndex]);

  // Update seek position and display time
  useEffect(() => {
    if (!activeChannel || isDragging) return;
    const interval = setInterval(() => {
      const video = videoRef.current;
      if (!video) return;

      // If video is paused, don't update time
      if (video.paused) return;

      if (catchupProgram && timeshiftStartTs !== null) {
        const currentRealTime = timeshiftStartTs + (video.currentTime || 0);
        const programDuration = catchupProgram.stop_timestamp - catchupProgram.start_timestamp;
        if (programDuration > 0) {
          const pos = (currentRealTime - catchupProgram.start_timestamp) / programDuration;
          setSeekPosition(Math.max(0, Math.min(1, pos)));
        }
        setCurrentDisplayTime(formatClockTime(currentRealTime));
      } else if (timeshiftStartTs !== null) {
        const currentRealTime = timeshiftStartTs + (video.currentTime || 0);
        const now = Date.now() / 1000;
        const offset = now - currentRealTime;
        const archiveSec = (activeChannel.tv_archive_duration || 0) * 3600;
        if (archiveSec > 0) setSeekPosition(Math.max(0, Math.min(1, 1 - offset / archiveSec)));
        setCurrentDisplayTime(formatClockTime(currentRealTime));
      } else {
        setCurrentDisplayTime(formatClockTime(Date.now() / 1000));
      }
    }, 1000);
    return () => clearInterval(interval);
  }, [activeChannel, timeshiftStartTs, isDragging, catchupProgram]);

  const filteredChannels = channels.filter((ch) => {
    const matchesCategory = selectedCategory ? ch.categories?.includes(parseInt(selectedCategory)) : true;
    const matchesSearch = searchQuery ? ch.name?.toLowerCase().includes(searchQuery.toLowerCase()) : true;
    return matchesCategory && matchesSearch;
  });

  const decodeBase64 = (str) => {
    try {
      const bytes = Uint8Array.from(atob(str), (c) => c.charCodeAt(0));
      return new TextDecoder("utf-8").decode(bytes);
    } catch { return str; }
  };

  const nowPlaying = epg.find((p) => p.now_playing === 1);
  const archiveSeconds = (activeChannel?.tv_archive_duration || 0) * 3600;
  const hasSeekBar = catchupProgram || archiveSeconds > 0;

  // Program time range for catch-up seek bar
  const programStartTime = catchupProgram ? formatClockTimeShort(catchupProgram.start_timestamp) : '';
  const programEndTime = catchupProgram ? formatClockTimeShort(catchupProgram.stop_timestamp) : '';

  // Current program info for controls bar
  const displayProgramTitle = catchupProgram?.title || (nowPlaying ? decodeBase64(nowPlaying.title) : '');

  if (loading) {
    return (
      <div data-testid="livetv-loading" className="flex items-center justify-center h-[calc(100vh-120px)]">
        <div className="flex flex-col items-center gap-3">
          <div className="w-10 h-10 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" />
          <span className="text-zinc-400 text-sm">Kanallar yuklenir...</span>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="livetv-page" className="flex flex-col md:flex-row gap-0 select-none relative h-[calc(100vh-64px)] md:h-[calc(100vh-24px)]">
      {/* Channel List — Mobile: below player (order-2) / Desktop: left side (order-1) */}
      <div
        data-testid="channel-panel"
        className="order-2 md:order-1 w-full md:w-72 flex-1 md:flex-none flex flex-col bg-zinc-900/80 md:border-r border-t md:border-t-0 border-zinc-800/50 overflow-hidden min-h-0 md:h-full"
      >
        {/* Search */}
        <div className="p-3 border-b border-zinc-800/50">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
            <input
              data-testid="channel-search"
              type="text"
              placeholder="Kanal axtar..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-9 pr-3 py-2 bg-zinc-800/80 border border-zinc-700/40 rounded-lg text-sm text-white placeholder-zinc-500 focus:outline-none focus:border-blue-500/50"
            />
          </div>
        </div>

        {/* Categories */}
        <div className="px-3 py-2 border-b border-zinc-800/50">
          <select
            data-testid="category-filter"
            value={selectedCategory || ""}
            onChange={(e) => setSelectedCategory(e.target.value || null)}
            className="w-full px-3 py-1.5 bg-zinc-800/80 border border-zinc-700/40 rounded-lg text-sm text-white focus:outline-none"
          >
            <option value="">{`Hamisi (${filteredChannels.length})`}</option>
            {categories.map((cat) => (
              <option key={cat.category_id} value={cat.category_id}>{cat.category_name}</option>
            ))}
          </select>
        </div>

        {/* Channel List */}
        <div ref={channelListRef} className="flex-1 overflow-y-auto">
          {filteredChannels.map((channel) => {
            const isActive = activeChannel?.stream_id === channel.stream_id;
            return (
              <div
                key={channel.stream_id}
                data-testid={`channel-item-${channel.stream_id}`}
                onClick={() => { playChannel(channel); setMobileChannelsOpen(false); }}
                className={`flex items-center gap-3 pl-0 pr-3 py-2.5 cursor-pointer transition-all duration-150 ${
                  isActive
                    ? "bg-blue-600/20 border-l-[3px] border-blue-400"
                    : "hover:bg-zinc-800/60 border-l-[3px] border-transparent"
                }`}
              >
                <div className="w-9 h-9 rounded-lg bg-zinc-800 overflow-hidden flex-shrink-0 border border-zinc-700/30">
                  {channel.stream_icon ? (
                    <img src={proxyImageUrl(channel.stream_icon)} alt="" className="w-full h-full object-contain p-0.5" loading="lazy" onError={(e) => { e.target.style.display = "none"; }} />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center"><Tv className="w-3.5 h-3.5 text-zinc-600" /></div>
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <p className={`text-sm truncate ${isActive ? "text-blue-300 font-semibold" : ""}`}>{channel.name}</p>
                  <p className="text-[10px] text-zinc-500">#{channel.num}</p>
                </div>
                {isActive && <span className="w-2 h-2 rounded-full bg-red-500 animate-pulse flex-shrink-0" />}
              </div>
            );
          })}
        </div>

        {/* EPG */}
        {activeChannel && epg.length > 0 && (
          <div className="border-t border-zinc-800/50 flex-shrink-0 h-[112px] md:h-48 overflow-y-auto bg-zinc-900/50" id="epg-scroll">
            <div data-epg-header className="px-3 py-1.5 flex items-center justify-between sticky top-0 bg-zinc-900/95 backdrop-blur-sm z-10">
              <div className="flex items-center gap-1.5 text-xs text-zinc-500">
                <Clock className="w-3 h-3" /> Proqram
              </div>
            </div>
            {epg.map((program, i) => {
              const isNow = program.now_playing === 1;
              const hasArchive = program.has_archive === 1;
              const startTs = parseInt(program.start_timestamp) || 0;
              const stopTs = parseInt(program.stop_timestamp) || 0;
              const nowSec = Math.floor(Date.now() / 1000);
              const isPast = !isNow && stopTs > 0 && stopTs < nowSec;
              const time = new Date(program.start).toLocaleTimeString("az-AZ", { hour: "2-digit", minute: "2-digit" });
              const isWatching = i === watchingProgramIndex;
              const nowProgress = isNow && stopTs > startTs
                ? Math.min(100, Math.max(0, ((nowSec - startTs) / (stopTs - startTs)) * 100))
                : null;
              return (
                <div
                  key={i}
                  data-now={isNow ? "true" : undefined}
                  data-watching={isWatching ? "true" : undefined}
                  data-testid={`epg-item-${i}`}
                  onClick={() => { if (isPast && hasArchive) playCatchup(program); }}
                  className={`relative px-3 py-1.5 text-xs flex items-center gap-2 transition-colors ${
                    isWatching
                      ? "text-blue-300 bg-blue-600/15"
                      : isNow
                      ? "text-blue-300 bg-blue-600/10"
                      : isPast && hasArchive
                      ? "text-zinc-400 hover:bg-zinc-800/60 cursor-pointer"
                      : "text-zinc-600"
                  }`}
                >
                  <span className="w-10 flex-shrink-0 font-mono">{time}</span>
                  <span className={`flex-1 truncate ${isWatching ? "font-semibold" : ""}`}>{decodeBase64(program.title)}</span>
                  {isNow && !isWatching && <span className="text-red-400 font-bold flex-shrink-0 text-[10px]">CANLI</span>}
                  {isWatching && isCatchup && <span className="text-blue-400 font-bold flex-shrink-0 text-[10px]">ARXIV</span>}
                  {isWatching && !isCatchup && isNow && <span className="text-red-400 font-bold flex-shrink-0 text-[10px]">CANLI</span>}
                  {isPast && hasArchive && !isWatching && (
                    <RotateCcw className="w-3 h-3 text-blue-400 flex-shrink-0" title="Catch-up" />
                  )}
                  {nowProgress !== null && (
                    <div data-testid={`epg-progress-${i}`} className="absolute bottom-0 left-0 right-0 h-px bg-zinc-700/40">
                      <div className="h-full bg-blue-400" style={{ width: `${nowProgress}%` }} />
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Player — Mobile: top (order-1) / Desktop: right (order-2) */}
      <div
        ref={containerRef}
        className="order-1 md:order-2 flex-shrink-0 md:flex-1 md:min-w-0 relative bg-black md:rounded-r-xl w-full aspect-video md:aspect-auto md:h-full md:w-auto"
        onClick={handleVideoClick}
        onMouseMove={handlePlayerMouseMove}
        onMouseEnter={handlePlayerMouseEnter}
        onMouseLeave={handlePlayerMouseLeave}
      >
        {/* Video wrapper - separate overflow-hidden for rounded corners */}
        <div className="absolute inset-0 overflow-hidden md:rounded-r-xl">
          <video ref={videoRef} data-testid="livetv-video" className="w-full h-full object-contain bg-black" playsInline />
          {/* Freeze frame canvas — shown briefly during channel switches to hide black flash */}
          <canvas
            ref={freezeCanvasRef}
            className="absolute inset-0 w-full h-full object-contain pointer-events-none"
            style={{ opacity: 0 }}
          />
        </div>

        {/* Loading spinner */}
        {streamLoading && (
          <div className="absolute inset-0 flex items-center justify-center pointer-events-none z-10">
            <div className="w-12 h-12 border-3 border-white/30 border-t-white rounded-full animate-spin" />
          </div>
        )}

        {/* Big Play/Pause overlay icon */}
        {showPlayPauseIcon && (
          <div className="absolute inset-0 flex items-center justify-center pointer-events-none z-20">
            <div className="w-20 h-20 rounded-full bg-black/50 flex items-center justify-center animate-fade-out">
              {showPlayPauseIcon === 'pause' ? (
                <Pause className="w-10 h-10 text-white" fill="white" />
              ) : (
                <Play className="w-10 h-10 text-white ml-1" fill="white" />
              )}
            </div>
          </div>
        )}

        {/* Bottom controls overlay */}
        <div
          data-controls="true"
          className={`absolute bottom-0 left-0 right-0 z-30 bg-gradient-to-t from-black/90 via-black/50 to-transparent transition-opacity duration-300 ${
            showControls ? "opacity-100" : "opacity-0 pointer-events-none"
          }`}
          onClick={(e) => e.stopPropagation()}
        >
          {/* "Canli" button - only in catch-up/timeshift */}
          {isCatchup && (
            <div className="px-4 pt-6">
              <button
                data-testid="back-to-live-overlay"
                onClick={backToLive}
                className="px-4 py-1.5 border border-zinc-400/50 rounded-md text-sm font-medium text-white hover:bg-white/10 transition-colors"
              >
                Canli
              </button>
            </div>
          )}

          {/* Seek bar */}
          {hasSeekBar && (
            <div data-testid="timeshift-container" className="px-4 pt-3 pb-1">
              {/* Program time labels for catch-up */}
              {catchupProgram && (
                <div className="flex justify-between text-[10px] text-zinc-500 mb-1 tabular-nums">
                  <span>{programStartTime}</span>
                  <span>{programEndTime}</span>
                </div>
              )}
              <div className="flex items-center gap-3">
                <div
                  ref={seekBarRef}
                  data-testid="timeshift-seekbar"
                  className="flex-1 relative h-5 flex items-center cursor-pointer group"
                  onMouseDown={handleSeekBarMouseDown}
                  onMouseMove={handleSeekBarHover}
                  onMouseLeave={handleSeekBarLeave}
                >
                  {/* Track */}
                  <div className="absolute left-0 right-0 h-[3px] group-hover:h-[5px] bg-zinc-600/60 rounded-full transition-all duration-150">
                    {/* Progress */}
                    <div
                      className="absolute left-0 top-0 h-full rounded-full transition-[width] duration-75"
                      style={{ width: `${seekPosition * 100}%`, background: 'linear-gradient(90deg, #3b82f6, #60a5fa)' }}
                    />
                    {/* Hover indicator */}
                    {seekHoverPos !== null && (
                      <div className="absolute top-0 h-full bg-white/15 rounded-full" style={{ width: `${seekHoverPos * 100}%` }} />
                    )}
                  </div>
                  {/* Thumb */}
                  <div
                    className="absolute w-3 h-3 bg-blue-400 rounded-full shadow-lg border-2 border-white/50 opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none"
                    style={{ left: `calc(${seekPosition * 100}% - 6px)` }}
                  />
                  {/* Hover indicator line on seekbar */}
                  {seekHoverPos !== null && (
                    <div className="absolute top-[-4px] w-[1px] h-[13px] bg-white/40 pointer-events-none" style={{ left: `${seekHoverPos * 100}%` }} />
                  )}
                  {/* Hover tooltip with thumbnail */}
                  {seekHoverPos !== null && (
                    <div
                      className="absolute bottom-7 pointer-events-none flex flex-col items-center"
                      style={{
                        left: `clamp(90px, ${seekHoverPos * 100}%, calc(100% - 90px))`,
                        transform: 'translateX(-50%)'
                      }}
                    >
                      {(() => {
                        const thumb = getClosestThumbnail(seekHoverPos);
                        return thumb ? (
                          <div className="mb-1 shadow-xl" style={{ borderRadius: '6px', border: '1px solid rgba(82,82,91,0.5)' }}>
                            <img
                              src={thumb.url}
                              alt=""
                              style={{ display: 'block', width: '160px', height: '90px', borderRadius: '6px', maxWidth: 'none', objectFit: 'fill' }}
                            />
                          </div>
                        ) : null;
                      })()}
                      <div className="px-2 py-0.5 bg-zinc-900/95 rounded text-[11px] text-zinc-200 whitespace-nowrap border border-zinc-700/50 font-mono">
                        {getHoverTime(seekHoverPos)}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Controls row */}
          <div className="px-3 pb-3 pt-1 flex items-center gap-2">
            {/* Play/Pause button */}
            <button
              data-testid="play-pause-btn"
              onClick={togglePlayPause}
              className="p-2 rounded-full hover:bg-white/15 transition-colors"
              title={isPlaying ? "Durdur" : "Oynat"}
            >
              {isPlaying ? <Pause className="w-5 h-5" /> : <Play className="w-5 h-5 ml-0.5" />}
            </button>

            {/* Volume — hidden on mobile (phone has its own volume buttons) */}
            <div className="hidden sm:flex items-center gap-1 group/vol relative">
              <button
                data-testid="mute-btn"
                onClick={toggleMute}
                className="p-2 rounded-full hover:bg-white/15 transition-colors"
                title={muted ? "Sesi ac" : "Sessiz edin"}
              >
                {muted ? <VolumeX className="w-5 h-5" /> : <Volume2 className="w-5 h-5" />}
              </button>
              <input
                data-testid="volume-slider"
                type="range"
                min="0"
                max="1"
                step="0.05"
                value={volume}
                onChange={(e) => changeVolume(parseFloat(e.target.value))}
                className="w-20 h-1 accent-blue-500 cursor-pointer"
              />
            </div>

            {/* Time display */}
            <span data-testid="time-display" className="text-sm text-zinc-300 tabular-nums font-mono ml-1">
              {currentDisplayTime}
            </span>

            {/* Status badge + Program title */}
            <div className="flex items-center gap-2 ml-2 flex-1 min-w-0">
              <span className={`flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-bold flex-shrink-0 ${
                isCatchup ? "bg-blue-600/60 text-blue-200" : "bg-red-600/60 text-red-200"
              }`}>
                {isCatchup ? "Arxiv" : "Canli"}
              </span>
              {displayProgramTitle && (
                <span data-testid="program-title" className="text-xs text-zinc-400 truncate">
                  {displayProgramTitle}
                </span>
              )}
            </div>

            {/* PiP */}
            <button
              data-testid="pip-btn"
              onClick={togglePiP}
              className="hidden sm:block p-2 rounded-full hover:bg-white/15 transition-colors"
              title="Kicik ekran"
            >
              <Monitor className="w-5 h-5" />
            </button>

            {/* Fullscreen */}
            <button
              data-testid="fullscreen-btn"
              onClick={toggleFullscreen}
              className="p-2 rounded-full hover:bg-white/15 transition-colors"
              title={isFullscreen ? "Kicilt" : "Tam ekran"}
            >
              {isFullscreen ? <Minimize className="w-5 h-5" /> : <Maximize className="w-5 h-5" />}
            </button>
          </div>
        </div>
      </div>

      {/* CSS animation for play/pause overlay */}
      <style>{`
        @keyframes fadeOut {
          0% { opacity: 1; transform: scale(1); }
          100% { opacity: 0; transform: scale(1.3); }
        }
        .animate-fade-out {
          animation: fadeOut 0.8s ease-out forwards;
        }
      `}</style>
    </div>
  );
}
